import { useEffect } from "react";

// Toolkit
import { FullScreenLoader } from "sj-ui-toolkit/src/components";

// Hooks
import { useLessonLoader } from "../../../hooks/useLessonLoader";

import { useTournamentContext } from "../../../context/TournamentContext";

// Sub-components
import SandboxLesson from "../../Lesson/SandboxLesson";
import VideoLesson from "../../Lesson/VideoLesson";

/**
 * TournamentLesson Component
 *
 * Renders a lesson within a tournament context. Uses the useLessonLoader hook
 * to fetch and display lesson details. Handles both video and sandbox (break/fix)
 * lesson types.
 */
export default function TournamentLesson({ onContentComplete }) {
  // Get the tournament context
  const { content, contentType, exerciseContent, subscribeToChannel } = useTournamentContext();
  const lessonKey = content?.lesson?.lesson?.lessonKey;
  const tournamentUuid = content?.tournament?.publicId;

  // Use the lesson loader hook
  const { lessonDetails } = useLessonLoader(lessonKey, content, exerciseContent);

  // Set up ActionCable subscriptions for tournament context
  useEffect(() => {
    if (lessonDetails) {
      if (lessonDetails.isBreakFix) {
        switch (lessonDetails.completionType) {
          case "tests":
            subscribeToChannel("tests");
            break;
          case "flagSubmission":
            subscribeToChannel("ctf");
            break;
          default:
            subscribeToChannel("default");
            break;
        }
      }
    }
  }, [lessonDetails, subscribeToChannel]);

  // When content is complete, tell the TournamentContentPage so it can navigate back
  const onLessonFinish = () => {
    onContentComplete();
  };

  // Return the full screen loader until we have lesson details
  if (!lessonDetails) {
    return (
      <div className="h-full">
        <FullScreenLoader />
      </div>
    );
  }

  // Render the appropriate lesson type
  if (lessonDetails.isBreakFix) {
    return (
      <SandboxLesson
        lessonDetails={lessonDetails}
        tournamentUuid={tournamentUuid}
        onLessonFinish={onLessonFinish}
      />
    );
  } else {
    return (
      <VideoLesson
        lessonDetails={lessonDetails}
        tournamentUuid={tournamentUuid}
        onLessonFinish={onLessonFinish}
        exerciseOnly={contentType === "Experiment"}
      />
    );
  }
}
